import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';
import {UserContext} from './components/UserContext.js';
import {CompanyContext} from './components/CompanyContext.js';
import AuthenticationService from './services/AuthenticationService';
import SignIn from './components/Users/SignIn'; 
import SignUp from './components/Users/SignUp'; 
import Dashboard from './components/Home/Dashboard'
import ProtectedRoute from './ProtectedRoute';
import NotFound404 from './components/Home/NotFound404';
import CandidateList from './components/Candidates/CandidateList';
import TestList from './components/Tests/TestList';
import UserList from './components/Team/UserList';
import TrackingCode from './TrackingCode';
import AddEditUser from './components/Team/AddEditUser';
import AddCandidate from './components/Candidates/AddCandidate';
import ImportCandidates from './components/Candidates/ImportCandidates';
import AddEditTest from './components/Tests/AddEditTest';
import Notifications from './components/Notifications/Notifications';
import Company from './components/Company/Settings';
import UserSettings from './components/Users/Settings'
import DeleteAccount from './components/Users/DeleteAccount'
import QuestionList from './components/Questions/QuestionList';
import AddEditQuestion from './components/Questions/AddEditQuestion';
import Result from './components/Candidates/Results/Result';
import PublicResult from './components/Candidates/Results/PublicResult';

import CompaniesService from './services/CompaniesService';
import {useMaintenanceStatus} from './hooks/useMaintenanceStatus.js';
import MaintenanceBar from './components/Shared/MaintenanceBar.js';

// Lazy load billing-related components
const Billing = React.lazy(() => import('./components/Company/Billing'));
const Receipt = React.lazy(() => import('./components/Company/Receipt'));
const CheckoutSuccess = React.lazy(() => import('./components/Company/CheckoutSuccess'));

// Loading component for lazy-loaded routes
const RouteLoading = () => (
  <div className="md:pl-64 flex flex-col flex-1">
    <div className="flex justify-center items-center h-screen bg-body">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
        <p className="text-gray-600 mt-4">Loading...</p>
      </div>
    </div>
  </div>
);


export default function App() {
  const currentUser = AuthenticationService.getCurrentUser();
  //console.log(currentUser)

  const [user, setUser] = useState(currentUser); 
  const [company, setCompany] = useState();   
  const [timeoutExecution, setTimeoutExecution] = useState(0); 
  const isMaintenance = useMaintenanceStatus();

  const setUserAndToken = (data) => {
    //console.log('set user hook: ');
    //console.log(data);

    // save token in cookie
    AuthenticationService.saveCurrentUser(data);

    // save candidate data
    setUser(data);
  }
  
  const setCompanyData = async()=> {
    if (user) {
      const comp = await CompaniesService.getCompany(user.accessToken.token);
      setCompany(comp)
      //console.log(comp)
    } else {
      setCompany(null)
    }

  }

  useEffect(() => {
    const fetchCompanyData = async () => {
      await setCompanyData();
    };
    fetchCompanyData();
  }, [user])  

  useEffect ( () => {
    //console.log(isMaintenance.maintenance)
    if (isMaintenance && isMaintenance.maintenance === true) {
      setUserAndToken(null)
      //console.log('disconnect user')
    }
  }, [isMaintenance]);

  // setting up a timer to refresh company data every hour (to check if the subscription is expired)
  useEffect(() => {
    const timeout = setTimeout( async () => {
      await setCompanyData()
      setTimeoutExecution(timeoutExecution + 1)
    }, 60 * 60 * 1000 * 4); // 4 hour timeout to refresh company data
  
    return () => {
      clearTimeout(timeout);

    }
  }, [timeoutExecution]);




  return (
    <div>

          {isMaintenance && isMaintenance.maintenance === true ? <MaintenanceBar reason={isMaintenance.reason}/> : <></>}

          <BrowserRouter>
          <TrackingCode />

            <UserContext.Provider value={{user, setUser: setUserAndToken}}>
              <CompanyContext.Provider value={{company, setCompany: setCompanyData}}>
                <Routes>
                    <Route path="/sign-in" element={<SignIn maintenance={isMaintenance} />} />     
                    <Route path="/sign-up" element={<SignUp maintenance={isMaintenance} />} />                     
                    <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                    <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                    <Route path="/candidates/add" element={<ProtectedRoute><AddCandidate /></ProtectedRoute>} />
                    <Route path="/candidates/import" element={<ProtectedRoute><ImportCandidates /></ProtectedRoute>} />
                    <Route path="/viewresult/:token" element={<PublicResult />} />                    
                    <Route path="/candidates/:id/result" element={<ProtectedRoute><Result /></ProtectedRoute>} />                    
                    <Route path="/candidates" element={<ProtectedRoute><CandidateList /></ProtectedRoute>} />                    
                    <Route path="/tests/add" element={<ProtectedRoute><AddEditTest /></ProtectedRoute>} />
                    <Route path="/tests/edit/:id" element={<ProtectedRoute><AddEditTest /></ProtectedRoute>} />
                    <Route path="/tests/:id/questions/edit/:questionId" element={<ProtectedRoute><AddEditQuestion /></ProtectedRoute>} />
                    <Route path="/tests/:id/questions/add/:questionId" element={<ProtectedRoute><AddEditQuestion /></ProtectedRoute>} />
                    <Route path="/tests/:id/questions/" element={<ProtectedRoute><QuestionList /></ProtectedRoute>} />
                    <Route path="/tests" element={<ProtectedRoute><TestList /></ProtectedRoute>} />
                    <Route path="/team/add" element={<ProtectedRoute><AddEditUser /></ProtectedRoute>} />
                    <Route path="/team/user/:id" element={<ProtectedRoute><AddEditUser /></ProtectedRoute>} />
                    <Route path="/team" element={<ProtectedRoute><UserList /></ProtectedRoute>} />
                    <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
                    <Route path="/company" element={<ProtectedRoute><Company /></ProtectedRoute>} />
                    <Route path="/billing/receipt/:id" element={
                      <ProtectedRoute>
                        <Suspense fallback={<RouteLoading />}>
                          <Receipt />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                    <Route path="/billing/success/:id" element={
                      <ProtectedRoute>
                        <Suspense fallback={<RouteLoading />}>
                          <CheckoutSuccess />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                    <Route path="/billing" element={
                      <ProtectedRoute>
                        <Suspense fallback={<RouteLoading />}>
                          <Billing />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                    <Route path="/settings" element={<ProtectedRoute><UserSettings /></ProtectedRoute>} />
                    <Route path="/deleteaccount" element={<ProtectedRoute><DeleteAccount /></ProtectedRoute>} />
                    <Route path="*" element={<NotFound404 />} />
                </Routes>
                </CompanyContext.Provider>
            </UserContext.Provider>

          </BrowserRouter>

    </div>

  )
}

